import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useInView, useReducedMotion } from "framer-motion";
import { Plant as PlantIcon, X, ArrowRight, ArrowCounterClockwise } from "@phosphor-icons/react";
import SmoothImg from "./SmoothImg";
import ScrollRow from "./ScrollRow";
import ArrivalScene from "./wizard/ArrivalScene";
import { resolveTheme } from "./wizard/sceneHoliday";
import { useSite } from "../lib/site";
import { fileUrl, focusStyle } from "../lib/api";
import { relativeTime } from "../lib/theme";

const WEEK = 7 * 86400000;
const KEY = "new-arrivals-dismissed";
const SEEN = "new-arrivals-scene-seen";

export const newThisWeek = (plants) => plants.filter((p) => p.status !== "deceased" && Date.now() - new Date(p.created_at).getTime() < WEEK)
  .sort((a, b) => b.created_at.localeCompare(a.created_at));

const ease = (t) => t * t * (3 - 2 * t);
const fade = (dir, a, b, unit, rise) => ({ background: `linear-gradient(${dir}, ${Array.from({ length: 13 }, (_, i) => { const t = i / 12; const o = rise ? ease(t) : 1 - ease(t); return `hsl(var(--card) / ${o.toFixed(3)}) ${(a + (b - a) * t).toFixed(1)}${unit}`; }).join(", ")})` });
const blurMask = (m) => ({ WebkitMaskImage: m, maskImage: m });

function Scrim() {
  return (
    <div aria-hidden className="absolute inset-0 pointer-events-none" data-testid="new-arrivals-scrim">
      <div className="absolute inset-0 lg:hidden" style={fade("to bottom", 172, 254, "px", true)} />
      <div className="absolute inset-0 hidden lg:block backdrop-blur-[3px]" style={blurMask("linear-gradient(to right, black 30%, transparent 50%)")} />
      <div className="absolute inset-0 hidden lg:block" style={fade("to right", 30, 60, "%")} />
    </div>
  );
}

function useTabVisible() {
  const [v, setV] = useState(() => document.visibilityState !== "hidden");
  useEffect(() => {
    const on = () => document.visibilityState !== "hidden" && setV(true);
    document.addEventListener("visibilitychange", on);
    return () => document.removeEventListener("visibilitychange", on);
  }, []);
  return v;
}

function SceneLayer({ newest, name, theme, play }) {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, amount: 0.45 });
  const ready = useTabVisible() && inView;
  const [animate, setAnimate] = useState(null);
  useEffect(() => {
    if (!ready || animate !== null) return;
    const fresh = !!newest && (localStorage.getItem(SEEN) || "") < newest;
    if (fresh) localStorage.setItem(SEEN, newest);
    setAnimate(fresh);
  }, [newest, ready, animate]);
  useEffect(() => { if (play) setAnimate(true); }, [play]);
  return (
    <div ref={ref} className="absolute inset-0 -z-10 rounded-[28px] lg:[clip-path:inset(0_round_28px)]" data-testid="new-arrivals-scene" data-ready={ready ? "1" : "0"}>
      {/* Below lg the scene only fills the top band, so nothing sits under the card's rounded bottom edge. */}
      <div className="absolute inset-x-0 top-0 h-[256px] lg:h-auto lg:bottom-0">
        {ready && animate !== null && <ArrivalScene key={play} name={name} still={!animate} theme={theme} className="after:absolute after:inset-0 after:pointer-events-none dark:after:bg-black/20" />}
      </div>
      <Scrim />
    </div>
  );
}

export default function NewArrivals({ plants, onSeeAll }) {
  const reduce = useReducedMotion();
  const list = newThisWeek(plants);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(KEY) || "");
  const newest = list[0]?.created_at || "";
  const site = useSite();
  const [play, setPlay] = useState(0);
  const replay = () => setPlay((n) => n + 1);
  if (!list.length || dismissed >= list[0].created_at) return null;
  const hide = () => { localStorage.setItem(KEY, list[0].created_at); setDismissed(list[0].created_at); };
  const theme = resolveTheme(new URLSearchParams(window.location.search).get("scene") || site.scene_theme, site.scene_holidays);
  return (
    <motion.section initial={reduce ? false : { opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ type: "spring", stiffness: 260, damping: 30 }}
      className="relative isolate mb-14 rounded-[28px] bg-card overflow-hidden lg:min-h-[460px] shadow-[0_1px_2px_rgba(0,0,0,0.04),0_8px_24px_-16px_rgba(0,0,0,0.12)]" data-testid="new-arrivals">
      <SceneLayer newest={newest} name={list[0].nickname} theme={theme} play={play} />
      <button type="button" onClick={replay} data-testid="new-arrivals-replay" aria-label="Replay the welcome"
        className="absolute top-3 right-3 lg:top-5 lg:right-5 z-10 h-8 px-3 rounded-full bg-background/85 lg:backdrop-blur-md text-xs font-medium text-muted-foreground hover:text-foreground inline-flex items-center gap-1 transition-colors">
        <ArrowCounterClockwise className="h-3.5 w-3.5" />Replay
      </button>
      <div className="min-w-0 px-5 pb-5 pt-[230px] sm:px-7 sm:pb-7 lg:pt-9 lg:pl-9 lg:w-[41%]">
        <div className="flex items-start justify-between gap-3">
          <div className="min-w-0">
            <p className="text-xs uppercase tracking-[0.18em] text-[#E8A100] font-medium">New this week</p>
            <h2 className="mt-1.5 text-2xl sm:text-3xl font-medium tracking-tight leading-tight" data-testid="new-arrivals-title">Say hello to your new arrivals</h2>
            <p className="mt-1 text-sm text-muted-foreground">{list.length === 1 ? "One plant joined" : `${list.length} plants joined`} the collection in the last 7 days.</p>
          </div>
          <button type="button" onClick={hide} aria-label="Hide new arrivals" data-testid="new-arrivals-dismiss"
            className="h-9 w-9 -mr-1 shrink-0 rounded-full inline-flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"><X className="h-4 w-4" /></button>
        </div>
        <div className="mt-6">
          <ScrollRow testid="new-arrivals-row" fade="mask" className="gap-3 sm:gap-4 pb-1 snap-mandatory" mx="-mx-5 sm:-mx-7 lg:-ml-9 lg:-mr-0" px="px-5 scroll-px-5 sm:px-7 sm:scroll-px-7 lg:pl-9 lg:pr-0">
            {list.slice(0, 12).map((p, i) => (
              <motion.div key={p.id} className="snap-start shrink-0" initial={reduce ? false : { opacity: 0, scale: 0.92 }} animate={{ opacity: 1, scale: 1 }} transition={{ type: "spring", stiffness: 340, damping: 26, delay: 0.08 + i * 0.05 }}>
                <Link to={`/plants/${p.id}`} className="group block w-28 sm:w-32" data-testid={`new-arrival-${p.id}`}>
                  <div className="aspect-square rounded-2xl bg-muted overflow-hidden isolate shadow-[0_8px_20px_-14px_rgba(0,0,0,0.35)]">
                    {p.photo_path ? <SmoothImg src={fileUrl(p.photo_path, 240)} alt={p.nickname} className="h-full w-full object-cover group-hover:scale-[1.05] transition-transform duration-500" style={focusStyle(p.photo_focus)} />
                      : <div className="h-full w-full flex items-center justify-center"><PlantIcon weight="duotone" className="h-8 w-8 text-muted-foreground/40" /></div>}
                  </div>
                  <p className="mt-2 text-sm font-medium truncate">{p.nickname}</p>
                  <p className="text-[11px] text-muted-foreground truncate">{relativeTime(p.created_at)}</p>
                </Link>
              </motion.div>
            ))}
          </ScrollRow>
        </div>
        {list.length > 1 && (
          <button type="button" onClick={onSeeAll} data-testid="new-arrivals-see-all"
            className="mt-5 h-9 px-4 rounded-full bg-muted/70 hover:bg-muted text-sm font-medium inline-flex items-center gap-1.5 transition-[background-color,transform] active:scale-95">
            See all {list.length}<ArrowRight className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
    </motion.section>
  );
}
